import { AirbridgeEvents } from '@healingpaper/shared'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { Icon, IconPack, Typography } from '@healingpaper/cell'
import { IcCheckMarkFilled, IcQuestionCircle } from '@healingpaper/cell-icons'
import { useCallback } from 'react'

import { useHospitalDetailContext } from '../hospital-detail-context'

import { postAmplitude } from '@/core/lib'
import { sendAirbridgeEvent } from '@/utils/airbridge'
import { clientWishlistService } from '@/features/wishlist/api/wishlist.service'
import { generateTagId } from '@/utils/test-automation'
import WishButton from '@/components/atomic/organisms/WishButton'
import cellTheme from '@/components/cell/designTokens'
import { useLocales } from '@/shared/hooks/useLocales'
import { createSignInUrl } from '@/shared/utils/url-utils'

const AssessmentBadgeSection = ({ className }: { className?: string }) => {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const isKorean = i18n.language === 'ko'

  const handleClickCertificate = () => {
    postAmplitude('click_info_certificate_in_hospital_detail')
    void router.push('/info/certificate')
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center gap-[2px] p-1 bg-[#F4F5FF] rounded-[4px] w-fit">
        <IcCheckMarkFilled color={cellTheme.colors.certified} width={16} height={16} />
        <Typography type="caption2" color="blue500" elementType="h4">
          {t('hospital.certifiedHospital')}
        </Typography>
      </div>
      {isKorean && <IcQuestionCircle onClick={handleClickCertificate} width={20} height={20} />}
    </div>
  )
}

export const HospitalDetailHeader = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const { countryPathnamePrefix, locale } = useLocales()
  const { user, hospital } = useHospitalDetailContext()

  const isAnonymousUser = !user || user.id === 0

  const onClickWishButton = (isWish: boolean) => {
    postAmplitude(isWish ? 'click_wish_in_hospital' : 'click_unwish_in_hospital')
    sendAirbridgeEvent(AirbridgeEvents.ADD_TO_WISH_LIST, {
      semanticAttributes: {
        type: 'HOSPITAL',
        products: [
          {
            brandID: hospital.hospitalId.toString(),
            brandName: hospital.name,
          },
        ],
      },
    })
  }

  const handleClickWish = async (isWish: boolean) => {
    if (isAnonymousUser) {
      void router.push(createSignInUrl(`/hospitals/${hospital.hospitalId}`, locale))
      return
    }

    if (isWish === true) {
      localStorage.setItem('hideWishGuideTooltip', 'true')
      await clientWishlistService.addWish(hospital.hospitalId)
      onClickWishButton(true)

      return
    }
    if (isWish === false) {
      await clientWishlistService.cancelWish(hospital.hospitalId)
      onClickWishButton(false)
    }
  }

  const initWish = useCallback(async () => {
    if (isAnonymousUser) {
      return false
    }

    const wishState = await clientWishlistService.hasWish(hospital.hospitalId)
    console.log('clientWishlistService.hasWish wishState', wishState)
    return wishState
  }, [hospital.hospitalId, isAnonymousUser])

  return (
    <div className="px-5 py-4">
      <div className="flex flex-col gap-2">
        <AssessmentBadgeSection className={hospital.assessment === 'CERTIFIED' ? 'visible' : 'hidden'} />
        <div className="flex flex-col gap-1">
          <div className="flex flex-row items-center justify-between">
            <Typography
              id={generateTagId('hospital', 'page', 'text', 'title', 'hospitalName')}
              elementType="h1"
              type="title2"
            >
              {hospital.name}
            </Typography>
            <WishButton
              id={generateTagId('hospital', 'page', 'btn', 'like')}
              initWish={initWish}
              className="flex items-center justify-center"
              text={{
                goToWishlistButton: t('templates.hospitalDetail.WishButton.goToWishlistButton'),
                wishGuide: t('templates.hospitalDetail.WishButton.wishGuide'),
                wishlistTitle: t('templates.hospitalDetail.WishButton.wishlistTitle'),
              }}
              onClickButton={() => {
                router.push(`/mypage/wishlist`)

                postAmplitude('click_wishlist_in_hospital')
              }}
              onClickWish={(isWish) => {
                handleClickWish(isWish)
              }}
            />
          </div>

          <Typography type="description2" color="gray800" elementType="h3">
            {hospital.location.sido}
            {hospital.location.subwayNames.length > 0 ? ` ・ ${hospital.location.subwayNames[0]}` : ''}
          </Typography>

          {!!hospital.statistic.reviewCount && (
            <div className="flex flex-row items-center gap-2">
              <div className="flex flex-row items-center gap-1">
                <Icon
                  name={IconPack.GENERAL_FEATURE_EVALUATION_FULL}
                  width={15}
                  height={15}
                  className="[&>*]:fill-yellow-400"
                />
                <Typography type="title4" elementType="span">
                  {hospital.statistic.rating.toFixed(1)}
                </Typography>
              </div>
              <Typography type="description2" color="gray800">
                {t('templates.hospitalDetail.reviewText', {
                  reviewCount: hospital.statistic.reviewCount.toLocaleString(),
                })}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
